import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaEnvelope, FaLock, FaEye, FaEyeSlash, FaShieldAlt } from 'react-icons/fa';
import AuthShell from '../components/auth/AuthShell';
import { useToast } from '../context/ToastContext';

const inputClass =
  'w-full rounded-xl border border-gray-200 bg-gray-50 py-3.5 pl-11 pr-4 text-sm text-gray-900 placeholder-gray-400 transition-all duration-200 focus:border-primary-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-neutral-700 dark:bg-neutral-900 dark:text-white dark:focus:bg-neutral-900';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showOtpForm, setShowOtpForm] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpCountdown, setOtpCountdown] = useState(0);
  const navigate = useNavigate();
  const toast = useToast();

  const startCountdown = (seconds) => {
    setOtpCountdown(seconds);
    const timer = setInterval(() => {
      setOtpCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/employee/login-initiate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Login failed');
      setShowOtpForm(true);
      startCountdown(120);
      toast.success(data.message || 'OTP sent to your email');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/employee/login-verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'OTP verification failed');
      localStorage.setItem('employeeToken', data.token);
      toast.success('Login successful! Redirecting...');
      setTimeout(() => navigate('/client'), 1000);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const resendOtp = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/employee/resend-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to resend OTP');
      startCountdown(120);
      toast.info('New OTP sent to your email');
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <AuthShell
      title={showOtpForm ? 'Verify OTP' : 'Employee Login'}
      subtitle={showOtpForm ? 'Enter the code sent to your email' : 'Welcome back! Please sign in to continue.'}
      footer={
        <p className="text-center text-sm text-gray-600 dark:text-gray-400">
          Don&apos;t have an account?{' '}
          <Link to="/signup" className="font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400">
            Sign Up
          </Link>
        </p>
      }
    >
      {!showOtpForm ? (
        <form onSubmit={handleLoginSubmit} className="space-y-4">
          <div className="relative group">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
              <FaEnvelope className="h-4 w-4 text-gray-400 group-focus-within:text-primary-500" />
            </div>
            <input
              type="email"
              className={inputClass}
              placeholder="Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="relative group">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
              <FaLock className="h-4 w-4 text-gray-400 group-focus-within:text-primary-500" />
            </div>
            <input
              type={showPassword ? 'text' : 'password'}
              className={`${inputClass} pr-11`}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400 hover:text-primary-500"
            >
              {showPassword ? <FaEyeSlash className="h-4 w-4" /> : <FaEye className="h-4 w-4" />}
            </button>
          </div>

          <div className="flex justify-end">
            <Link to="/forgetpassword" className="text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400">
              Forgot Password?
            </Link>
          </div>

          <button type="submit" disabled={loading} className="btn-premium w-full py-3.5">
            {loading ? 'Sending OTP...' : 'Login'}
          </button>
        </form>
      ) : (
        <form onSubmit={handleOtpSubmit} className="space-y-4">
          <div className="relative group">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
              <FaShieldAlt className="h-4 w-4 text-gray-400 group-focus-within:text-primary-500" />
            </div>
            <input
              type="text"
              className={inputClass}
              placeholder="Enter OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              required
            />
          </div>

          <button type="submit" disabled={loading} className="btn-premium w-full py-3.5">
            {loading ? 'Verifying...' : 'Verify OTP'}
          </button>

          {otpCountdown > 0 ? (
            <p className="text-center text-sm text-gray-500 dark:text-gray-400">
              Resend OTP available in {formatTime(otpCountdown)}
            </p>
          ) : (
            <button type="button" onClick={resendOtp} className="w-full text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400">
              Resend OTP
            </button>
          )}

          <button
            type="button"
            onClick={() => setShowOtpForm(false)}
            className="w-full text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400"
          >
            ← Back to login
          </button>
        </form>
      )}
    </AuthShell>
  );
};

export default Login;
